import { doc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firestore';
import { Element as ElementType } from '@/types';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

// Get Firebase Functions instance
const functions = getFunctions();

// Report an issue
interface ReportResult {
  success: boolean;
  emailSent: boolean;
  reportId: string;
  warnings: string[];
}

export async function reportIssue(
  userId: string,
  userEmail: string,
  description: string
): Promise<ReportResult> {
  try {
    // Add to Firestore - simplified path
    const docRef = await addDoc(collection(db, 'help_reports'), {
      userId,
      userEmail,
      description,
      timestamp: serverTimestamp(),
      status: 'new',
      type: 'issue',
      category: 'issues',
    });

    

    // Return object to indicate success status and any warnings
    const result = {
      success: true,
      emailSent: false,
      reportId: docRef.id,
      warnings: [] as string[],
    };

    // Call Cloud Function to send email (HTTP version)
    try {
      // Get a fresh ID token
      const auth = getAuth();
      if (auth.currentUser) {
        const idToken = await auth.currentUser.getIdToken(true);

        // Call the HTTP function directly
        const response = await fetch(
          'https://us-central1-vibecraft-5c54c.cloudfunctions.net/sendReportEmailHttp',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              type: 'issue',
              userId,
              userEmail,
              description,
              timestamp: new Date().toISOString(),
              reportId: docRef.id,
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! Status: ${response.status}, Details: ${errorText}`);
        }

        const emailResult = await response.json();
        
        result.emailSent = true;
      } else {
        
        result.warnings.push('No authenticated user found for email notification');
      }
    } catch (error) {
      
      // Add warning but don't fail the overall operation
      result.warnings.push(
        `Email notification failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }

    
    return result;
  } catch (error) {
    
    throw error;
  }
}

// Report an element
export async function reportElement(
  userId: string,
  userEmail: string,
  element: ElementType,
  description: string = ''
): Promise<ReportResult> {
  try {
    // Add to Firestore - simplified path
    const docRef = await addDoc(collection(db, 'help_reports'), {
      userId,
      userEmail,
      elementId: element.id,
      elementName: element.name,
      elementType: element.type,
      description,
      timestamp: serverTimestamp(),
      status: 'new',
      type: 'element',
      category: 'elements',
    });

    

    // Return object to indicate success status and any warnings
    const result = {
      success: true,
      emailSent: false,
      reportId: docRef.id,
      warnings: [] as string[],
    };

    // Call Cloud Function to send email (HTTP version)
    try {
      // Get a fresh ID token
      const auth = getAuth();
      if (auth.currentUser) {
        const idToken = await auth.currentUser.getIdToken(true);

        // Call the HTTP function directly
        const response = await fetch(
          'https://us-central1-vibecraft-5c54c.cloudfunctions.net/sendReportEmailHttp',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              type: 'element',
              userId,
              userEmail,
              elementId: element.id,
              elementName: element.name,
              elementType: element.type,
              description,
              timestamp: new Date().toISOString(),
              reportId: docRef.id,
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! Status: ${response.status}, Details: ${errorText}`);
        }

        const emailResult = await response.json();
        
        result.emailSent = true;
      } else {
        
        result.warnings.push('No authenticated user found for email notification');
      }
    } catch (error) {
      
      // Add warning but don't fail the overall operation
      result.warnings.push(
        `Email notification failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }

    
    return result;
  } catch (error) {
    
    throw error;
  }
}

